import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Trophy, Receipt, Users, Wallet, ClockCounterClockwise, ListChecks } from '@phosphor-icons/react';

// Phase 4 — Staff Performance + Productivity. Admin sees the full leaderboard;
// staff sees their own row only. Click a row to expand productivity details +
// component-score bars.

const BUCKET_STYLES = {
  excellent:         { label: 'Excellent',         cls: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]', bar: '#10B981' },
  good:              { label: 'Good',              cls: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]', bar: '#3B82F6' },
  average:           { label: 'Average',           cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]', bar: '#F59E0B' },
  needs_improvement: { label: 'Needs Improvement', cls: 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]', bar: '#EF4444' },
};

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-[#E4E4E7] rounded-xl p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-[#52525B]" />
        <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">{label}</p>
      </div>
      <p className="text-xl font-bold text-[#0A0A0A] font-mono">{value}</p>
      {sub && <p className="text-[10px] text-[#A1A1AA] mt-0.5">{sub}</p>}
    </div>
  );
}

function ScoreBar({ label, value, weight }) {
  const v = Math.min(100, Math.max(0, value || 0));
  return (
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-[#52525B]">{label} <span className="text-[#A1A1AA]">({weight}%)</span></span>
        <span className="font-mono font-semibold">{v}%</span>
      </div>
      <div className="h-2 rounded-full bg-[#F4F4F5] overflow-hidden">
        <div className="h-full rounded-full bg-[#3B82F6]" style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function DetailDialog({ row, onClose }) {
  if (!row) return null;
  const meta = BUCKET_STYLES[row.bucket] || BUCKET_STYLES.average;
  const p = row.productivity || {};
  return (
    <Dialog open={!!row} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy size={18} className="text-[#F59E0B]" /> {row.user_name}
            <Badge className={`rounded-md text-[10px] ${meta.cls}`} variant="outline">{meta.label}</Badge>
          </DialogTitle>
          <DialogDescription>{row.designation || 'Staff'} — {row.month} performance breakdown</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 mt-2">
          <div className="text-5xl font-bold font-mono" style={{ color: meta.bar }}>{row.score}</div>
          <div className="flex-1 space-y-2">
            {Object.entries(row.components || {}).map(([k, v]) => {
              const weight = row.weights?.[k.split('_')[0]] ?? 10;
              const labels = {
                attendance_discipline: 'Attendance Discipline',
                services_score: 'Services Completed',
                revenue_score: 'Revenue',
                pending_clearance: 'Pending Clearance',
                on_time_completion: 'On-Time Completion',
              };
              return <ScoreBar key={k} label={labels[k] || k} value={v} weight={weight} />;
            })}
          </div>
        </div>
        <div className="border-t border-[#F4F4F5] pt-3 mt-2">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B] mb-2">Productivity Metrics</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat icon={Receipt} label="Bills Created" value={p.bills_created || 0} />
            <Stat icon={Wallet} label="Revenue" value={fmtINR(p.revenue_generated)} />
            <Stat icon={Users} label="Customers" value={p.customers_handled || 0} />
            <Stat icon={ListChecks} label="Services Done" value={p.services_completed || 0} sub={`${p.work_pending || 0} pending`} />
            <Stat icon={ListChecks} label="Tokens" value={p.tokens_handled || 0} />
            <Stat icon={ListChecks} label="Follow-ups" value={p.followups_completed || 0} />
            <Stat icon={ClockCounterClockwise} label="Avg Time" value={`${p.avg_completion_hours || 0}h`} sub="per service" />
            <Stat icon={ListChecks} label="Complaints" value={p.complaints || 0} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PerformancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/performance/score?month=${month}`)
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [month]);

  const topStaff = isAdmin ? rows.slice(0, 3) : [];
  const lowStaff = isAdmin ? [...rows].filter(r => r.bucket === 'needs_improvement') : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-[#52525B]">
          {isAdmin
            ? 'Weighted score: 30% attendance · 25% services · 20% revenue · 15% clearance · 10% on-time.'
            : 'Your monthly performance score and productivity breakdown.'}
        </p>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg w-40 h-8 text-xs" data-testid="perf-month" />
        </div>
      </div>

      {isAdmin && topStaff.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#15803D] mb-2">🏆 Top Performers</p>
              <div className="space-y-2">
                {topStaff.map((r, i) => (
                  <div key={r.user_id} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[#A1A1AA] text-sm w-5">#{i + 1}</span>
                      <span className="font-medium truncate">{r.user_name}</span>
                    </div>
                    <Badge className={`rounded-md text-[10px] ${BUCKET_STYLES[r.bucket].cls}`} variant="outline">{r.score} · {BUCKET_STYLES[r.bucket].label}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {lowStaff.length > 0 && (
            <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#B91C1C] mb-2">⚠ Needs Attention</p>
                <div className="space-y-2">
                  {lowStaff.slice(0, 5).map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{r.user_name}</span>
                      <Badge className={`rounded-md text-[10px] ${BUCKET_STYLES[r.bucket].cls}`} variant="outline">{r.score}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4">
          {loading ? <p className="text-sm text-[#52525B] py-6 text-center">Loading…</p> : rows.length === 0 ? (
            <p className="text-sm text-[#52525B] py-6 text-center">No data for this month.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((r, i) => {
                const meta = BUCKET_STYLES[r.bucket] || BUCKET_STYLES.average;
                const p = r.productivity || {};
                return (
                  <button
                    type="button"
                    key={r.user_id}
                    onClick={() => setDetail(r)}
                    data-testid={`perf-row-${r.user_id}`}
                    className="w-full text-left border border-[#E4E4E7] rounded-xl p-3 hover:bg-[#FAFAFA] transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[#A1A1AA] text-sm w-6">#{i + 1}</span>
                      {r.photo ? (
                        <img src={r.photo} alt={r.user_name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-xs font-bold">{(r.user_name || '?').charAt(0)}</div>
                      )}
                      <div className="flex-1 min-w-[160px]">
                        <p className="text-sm font-semibold">{r.user_name}</p>
                        <p className="text-[10px] text-[#A1A1AA]">{r.designation || 'Staff'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-mono" style={{ color: meta.bar }}>{r.score}</p>
                        <Badge className={`rounded-md text-[10px] ${meta.cls}`} variant="outline">{meta.label}</Badge>
                      </div>
                      <div className="hidden md:grid grid-cols-4 gap-3 ml-4 flex-1 max-w-[420px]">
                        <div><p className="text-[9px] uppercase text-[#A1A1AA]">Bills</p><p className="font-mono font-semibold">{p.bills_created || 0}</p></div>
                        <div><p className="text-[9px] uppercase text-[#A1A1AA]">Revenue</p><p className="font-mono font-semibold">{fmtINR(p.revenue_generated)}</p></div>
                        <div><p className="text-[9px] uppercase text-[#A1A1AA]">Svcs Done</p><p className="font-mono font-semibold">{p.services_completed || 0}</p></div>
                        <div><p className="text-[9px] uppercase text-[#A1A1AA]">Avg Time</p><p className="font-mono font-semibold">{p.avg_completion_hours || 0}h</p></div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <DetailDialog row={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
